import ApiService from './api.service';

const TributePhotoService = token => {
    const api = ApiService.createApiInstance(token, { enableBugsnag: true });

    return {
        createPhotoBatch(eventId, photoBatchData, replaceMainPhoto = false, config = {}) {
            return api.post(`/TributeVideoPhoto/${eventId}?replaceMain=${replaceMainPhoto}`, photoBatchData, {
                ...config,
            });
        },
        deletePhoto(id) {
            return api.delete(`/TributeVideoPhoto/${id}`);
        },
        deletePhotoBatch(ids) {
            return api.put(`/TributeVideoPhoto/batch-delete`, ids);
        },
        deletePhotoBatchSession(ids, sessionId) {
            return api.put(`/TributeVideoPhoto/batch-delete/${sessionId}`, ids);
        },
        deletePhotoBatchByGuid(guids) {
            return api.put(`/TributeVideoPhoto/batch-delete-by-guid`, guids);
        },
        deletePhotoByGuid(guid) {
            return api.delete(`/TributeVideoPhoto/unique-delete/${guid}`);
        },
        getCollectionDuplicates(serviceId) {
            return api.get(`/TributeVideoPhoto/find-collection-duplicates/${serviceId}`);
        },
        getCollectionSimilars(serviceId) {
            return api.get(`/TributeVideoPhoto/find-collection-similars/${serviceId}`);
        },
        getCollectionUrls(tributeId, config = {}) {
            return api.get(`/TributeVideoPhoto/get-collection-urls/${tributeId}`, config);
        },
        getUploadUrl(eventId, file) {
            return api.get(`/TributeVideoPhoto/sas/${eventId}/${file.name}`);
        },
        updatePhotoFileName(id, fileName) {
            return api.put(`/TributeVideoPhoto/${id}`, JSON.stringify(fileName));
        },
        getRemainingAllowedUploads(eventId, sessionId) {
            return api.get(`/TributeVideoPhoto/remaining-allowed-uploads/${eventId}`, {
                params: {
                    sessionId,
                },
            });
        },
        updateProfilePhoto(eventId, photoId) {
            return api.post(`/TributeVideoPhoto/update-main-photo/${eventId}/${photoId}`);
        },
        getPhotoById(photoId) {
            if (photoId) {
                return api.get(`/TributeVideoPhoto/${photoId}`);
            }
        },
        getPhotos(eventId, params) {
            let stringParams = new URLSearchParams();
            if (params?.uploadUsers) {
                params.uploadUsers.forEach(user => {
                    stringParams.append('uploadUserName', user.uploadUserName);
                    stringParams.append('uploadUserRelationship', user.uploadUserRelationship);
                });

                delete params.uploadUsers;
            }

            return api.get(`/TributeVideoPhoto/event-photos/${eventId}?${stringParams.toString()}`, {
                params: params,
            });
        },
        getSessionPhotos(eventId, sessionId, params) {
            return api.get(`/TributeVideoPhoto/event-photos/${eventId}/${sessionId}`, {
                params: params,
            });
        },
        revertPhotoToOriginal(photoId) {
            return api.post(`/TributeVideoPhoto/revert-original-photo/${photoId}`);
        },
        updateOrder(id, targetIndex) {
            return api.put(`/TributeVideoPhoto/update-order/${id}/${targetIndex}`);
        },
        multiUpdateOrder(tributeId, startingOrder, slideIds) {
            //TODO: include tributeId in  request after endpoint is updated
            return api.put(`/TributeVideoPhoto/multi-update-order/${tributeId}/${startingOrder}`, slideIds);
        },
        replacePhoto(id, fileName, keepOriginal, sessionId = null, duration) {
            let data = { fileName, sessionId };
            if (duration) {
                // TODO: remove rounding when backend supports float/precision for durations
                data.duration = Math.round(duration)
            }
            return api.put(`/TributeVideoPhoto/replace-photo/${id}?keepOriginal=${keepOriginal}`, data);
        },
        getSlideSceneUploadUrl(slideId) {
            return api.get(`/TributeVideoPhoto/slide-scene/sas/${slideId}`);
        },
        updateSlideScene(slideId, fileName, sessionId = null) {
            return api.post(`/TributeVideoPhoto/slide-scene/${slideId}/${fileName}?sessionId=${sessionId}`);
        },
        setVideoSlideMute(slideId, bool) {
            return api.post(`/TributeVideoPhoto/toggle-slide-mute/${slideId}/${bool}`);
        },
        ingestPipelineStatus(eventId) {
            return api.get(`/Admin-Tools/tribute-slide-ingest-pipeline-status/${eventId}`);
        },
        ingestPipelineCount(eventId) {
            return api.get(`/TributeVideoPhoto/ingest-pipeline-current-count/${eventId}`);
        },
        getUploadUsers(tributeId) {
            return api.get(`/TributeVideoPhoto/upload-users/${tributeId}`);
        },
        triggerRotatePhotoFunction(photoIds, rotation) {
            return api.post(`/TributeVideoPhoto/trigger-photo-rotate`, {
                ids: photoIds,
                rotation,
            });
        },
        updateSlideDuration(slideId, duration) {
            if (!slideId) {
                return Promise.reject('Slide ID is required');
            }
            return api.post(`/TributeVideoPhoto/slide-duration/${slideId}?duration=${duration || ''}`);
        },
        // Editor Assets

        // Slide templates
        getSlideTemplates(params = {}) {
            return api.get(`/Editor/assets/slide-template`, { params });
        },
        // Funeral Home
        getUploadUrlForFuneralHomeAsset(funeralHomeId, fileName) {
            return api.get(`/Editor/assets/funeral-home/sas/${funeralHomeId}/${fileName}`);
        },
        getFuneralHomeAssets(funeralHomeId, params = {}) {
            return api.get(`/Editor/assets/funeral-home/${funeralHomeId}`, { params });
        },
        postFuneralHomeAsset(funeralHomeId, assetPayload) {
            return api.post(`/Editor/assets/funeral-home/${funeralHomeId}`, assetPayload);
        },
        // Tribute Event
        getUploadUrlForTributeEventEditorAsset(eventId, fileName) {
            return api.get(`/Editor/assets/event/sas/${eventId}/${fileName}`);
        },
        getTributeEventEditorAssets(eventId, params = {}) {
            return api.get(`/Editor/assets/event/${eventId}`, { params });
        },
        postTributeEventEditorAsset(eventId, assetPayload) {
            return api.post(`/Editor/assets/event/${eventId}`, assetPayload);
        },
        // Delete Asset
        deleteEditorAssetByID(assetId) {
            return api.delete(`/Editor/assets/${assetId}`);
        },
    };
};

export default TributePhotoService;
